import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router'
import { Box, EnumHelperText, HelperText, Tabs, Typography } from 'shared/ui'
import { MessageField } from 'widgets/MessageField'
import { useChatbotContext } from 'pages/chatbot/context'
import { AgentFooterButtons } from 'pages/chatbot/ui/ChatbotButtons/ChatbotButtons'
import { useBookAppointmentBlocker } from 'pages/chatbot/pages/agentBookAppointment/hooks/useBookAppointmentBlocker'
import { AgentKnowledgeBase } from 'pages/chatbot/ui/AgentKnowledgeBase/AgentKnowledgeBase'
import styles from './styles.module.scss'

export const SettingAgent = observer(() => {
  const { agentId: id } = useParams()
  const {
    errors,
    model: { settings, setKnowledgeBaseDocuments, knowledgeBaseDocuments },
    agentKnowledgeBaseStore,
    agentBookAppointmentStepsStore: {
      settingAgentStore,
      isExistChanges,
      confirmLeavePage,
      agentActivateStore: { handleActivateClickWithModal },
    },
    model: { isActive },
  } = useChatbotContext()

  const {
    messageFieldStore,
    conversationType,
    setConversationType,
    isAutoConvType,
    tabs,
    handleNextClick,
    errorsMap,
    handleSaveDraft,
    removeError,
    disabled,
  } = settingAgentStore

  useBookAppointmentBlocker({
    isExistChanges,
    confirmLeavePage: (blocker) =>
      confirmLeavePage(
        blocker,
        id,
        isActive
          ? async (id) => {
              const result = await handleActivateClickWithModal(id, {
                isPublished: isActive,
                shouldRedirect: false,
              })
              return result
            }
          : handleSaveDraft
      ),
  })

  const errorMessage = errorsMap.get('first_message.message')

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      height={'100%'}
      justifyContent={'space-between'}
      paddingLeft={'20px'}
      paddingRight={'20px'}
    >
      <Box marginTop={'32px'}>
        <AgentKnowledgeBase
          agentKnowledgeBaseStore={agentKnowledgeBaseStore}
          knowledgeBaseUsageError={{
            message: errors.getError('settings.use_knowledge_base.value') ?? '',
            resetError: () => errors.removeError('settings.use_knowledge_base'),
          }}
          knowledgeBaseDocumentsError={{
            message: errorsMap.get('knowledge_base_documents') ?? '',
            resetError: () => removeError('knowledge_base_documents'),
          }}
          setKnowledgeBaseUsage={settings.setKnowledgeBaseUsage}
          knowledgeBaseUsage={settings.knowledgeBaseUsage}
          setKnowledgeBaseDocuments={setKnowledgeBaseDocuments}
          knowledgeBaseDocuments={knowledgeBaseDocuments}
        />
        <Typography
          variant={'body-lg-medium'}
          ariaLabel={'ConversationTitle'}
          color={'var(--content-primary-primary)'}
          marginBottom={4}
        >
          How should the conversation start?
        </Typography>
        <div className={styles.tabs}>
          <Tabs
            variant={'simpleWithoutBorder'}
            tabs={tabs}
            activeTabKey={conversationType}
            handleSelectTab={({ key }) => setConversationType(key)}
          />
        </div>
        {isAutoConvType ? (
          <div className={styles.autoConvMessage}>
            <Typography variant={'body-md-regular'} ariaLabel={'continueConv_message'}>
              AI Agent will continue conversations naturally based on past messages, or initiate new
              conversations when no prior context exists.
            </Typography>
          </div>
        ) : (
          <div className={styles.messageField}>
            <MessageField messageFieldStore={messageFieldStore} />
            {!!errorMessage && (
              <HelperText
                icon={'alertTriangleSmall'}
                variant={EnumHelperText.Error}
                text={errorMessage}
              />
            )}
          </div>
        )}
      </Box>
      <AgentFooterButtons
        handleNextClick={() => handleNextClick(id)}
        isNextButtonDisabled={disabled}
        isFirstStep
      />
    </Box>
  )
})
